import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ArrowLeft,
  Save,
  Play,
  Plus,
  FileText,
  TrendingUp,
  RefreshCw,
  Trash2,
  Download,
  MoreVertical,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { exportToCSV, exportToExcel, generateFilename, formatCurrencyForExport, type ExportColumn } from '@/lib/export-utils';
import { ConfirmationModal } from '@/components/ui/confirmation-modal';
import type { RevaluationBatch, RevaluationMode, RevaluationItem } from '@/types/revaluation';
import { RevaluationBatchGrid } from '@/components/revaluation/revaluation-batch-grid';
import { dataService } from '@/services/data-service';
import { formatCurrency } from '@/lib/format-currency';
import { formatApiErrorMessage } from '@/lib/format-utils';

const getRevaluationExportColumns = (): ExportColumn<RevaluationItem>[] => [
  { label: 'Asset Code', key: 'assetCode' as keyof RevaluationItem },
  { label: 'Asset Description', key: 'assetDescription' as keyof RevaluationItem },
  { label: 'Category', key: 'category' as keyof RevaluationItem },
  { label: 'Location', key: 'location' as keyof RevaluationItem },
  { label: 'Current NBV', key: 'currentNBV' as keyof RevaluationItem, format: (v: number) => formatCurrencyForExport(v) },
  { label: 'AI Revalued NBV', key: 'aiRevaluedNBV' as keyof RevaluationItem, format: (v: number) => formatCurrencyForExport(v) },
  { label: 'Revalued NBV', key: 'revaluedNBV' as keyof RevaluationItem, format: (v: number) => formatCurrencyForExport(v) },
  { label: 'Revaluation Adjustment', key: 'revaluationAdjustment' as keyof RevaluationItem, format: (v: number) => formatCurrencyForExport(v) },
  { label: 'Confidence', key: 'confidence' as keyof RevaluationItem },
  { label: 'Notes', key: 'notes' as keyof RevaluationItem },
];
import { 
  validateRevaluationAmount, 
  getRevaluationAmountPlaceholder,
  recalculateRevaluationItems 
} from '@/lib/revaluation-calculator';

interface RevaluationEntryScreenProps {
  batch: RevaluationBatch;
  onClose: () => void;
  onSave: (batch: RevaluationBatch) => void;
}

export function RevaluationEntryScreen({
  batch: initialBatch,
  onClose,
  onSave,
}: RevaluationEntryScreenProps): React.ReactElement {
  const navigate = useNavigate();
  
  // State for batch header
  const [batchReference, setBatchReference] = useState(initialBatch?.batchReference ?? '');
  const [dateOfRun, setDateOfRun] = useState(
    initialBatch?.dateOfRun || new Date().toISOString().split('T')[0]
  );
  const [revaluationMode, setRevaluationMode] = useState<RevaluationMode>(
    initialBatch?.revaluationMode || 'ByPercentage'
  );
  const [revaluationAmount, setRevaluationAmount] = useState(
    initialBatch?.revaluationAmount || 0
  );
  const [notes, setNotes] = useState(initialBatch?.notes ?? '');
  
  // State for revaluation items
  const [items, setItems] = useState(initialBatch?.items || []);
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set());
  
  // State for modals
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showProcessConfirm, setShowProcessConfirm] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Computed values
  const isReadOnly = initialBatch?.status === 'Completed';
  const totalCurrentNBV = items.reduce((sum, item) => sum + item.currentNBV, 0);
  const totalRevaluedNBV = items.reduce((sum, item) => sum + item.revaluedNBV, 0);
  const totalRevaluationAdjustment = items.reduce((sum, item) => sum + item.revaluationAdjustment, 0);

  // Mark as having unsaved changes when any field changes (skip initial render)
  const [isInitialRender, setIsInitialRender] = useState(true);
  
  useEffect(() => {
    if (isInitialRender) {
      setIsInitialRender(false);
      return;
    }
    setHasUnsavedChanges(true);
  }, [batchReference, dateOfRun, revaluationMode, revaluationAmount, notes, items]);

  const handleSave = async () => {
    // Validate required fields
    if (!batchReference.trim()) {
      toast.error('Reference is required', {
        description: 'Please enter a batch reference before saving.',
      });
      return;
    }

    if (!dateOfRun) {
      toast.error('Date is required', {
        description: 'Please select a date before saving.',
      });
      return;
    }

    if (items.length === 0) {
      toast.error('No assets selected', {
        description: 'Please add at least one asset to the revaluation batch.',
      });
      return;
    }

    // Validate that at least one item has a non-zero revaluation adjustment
    const hasValidItems = items.some(item => item.revaluationAdjustment !== 0);
    if (!hasValidItems) {
      toast.error('No revaluation adjustments', {
        description: 'Please ensure at least one asset has a non-zero revaluation adjustment.',
      });
      return;
    }

    const now = new Date().toISOString();
    
    const batch: RevaluationBatch = {
      id: initialBatch?.id || `batch-${Date.now()}`,
      batchReference,
      dateOfRun,
      revaluationMode,
      revaluationAmount,
      notes,
      items,
      status: 'In Progress',
      preparedBy: initialBatch?.preparedBy || 'Current User',
      preparedDate: initialBatch?.preparedDate || now,
      totalCurrentNBV,
      totalRevaluedNBV,
      totalRevaluationAdjustment,
      currency: 'GBP',
      createdBy: initialBatch?.createdBy || 'Current User',
      createdDate: initialBatch?.createdDate || now,
      modifiedBy: 'Current User',
      modifiedDate: now,
      currentStep: 'Prepare',
    };

    try {
      onSave(batch);
      setHasUnsavedChanges(false);
      toast.success('Batch saved successfully');
    } catch (error) {
      console.error('Failed to save revaluation batch:', error);
      toast.error('Failed to save batch', {
        description: 'Please try again.',
      });
    }
  };

  const handleProcess = () => {
    // Validate required fields
    if (!batchReference.trim()) {
      toast.error('Reference is required', {
        description: 'Please enter a batch reference before processing.',
      });
      return;
    }

    if (!dateOfRun) {
      toast.error('Date is required', {
        description: 'Please select a date before processing.',
      });
      return;
    }

    if (items.length === 0) {
      toast.error('No assets selected', {
        description: 'Please add at least one asset to the revaluation batch.',
      });
      return;
    }

    // Validate that at least one item has a non-zero revaluation adjustment
    const hasValidItems = items.some(item => item.revaluationAdjustment !== 0);
    if (!hasValidItems) {
      toast.error('No revaluation adjustments', {
        description: 'Please ensure at least one asset has a non-zero revaluation adjustment.',
      });
      return;
    }

    setShowProcessConfirm(true);
  };

  const confirmProcess = async () => {
    try {
      const reference = batchReference;
      
      // Ensure batch is saved first
      const isServerGeneratedId = (id: string) => {
        const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        return guidRegex.test(id);
      };

      let batchId = initialBatch?.id;
      
      if (!isServerGeneratedId(initialBatch?.id || '')) {
        const now = new Date().toISOString();
        const batchToSave: RevaluationBatch = {
          id: initialBatch?.id || `batch-${Date.now()}`,
          batchReference,
          dateOfRun,
          revaluationMode,
          revaluationAmount,
          notes,
          items,
          status: 'In Progress',
          preparedBy: initialBatch?.preparedBy || 'Current User',
          preparedDate: initialBatch?.preparedDate || now,
          totalCurrentNBV,
          totalRevaluedNBV,
          totalRevaluationAdjustment,
          currency: 'GBP',
          createdBy: initialBatch?.createdBy || 'Current User',
          createdDate: initialBatch?.createdDate || now,
          modifiedBy: 'Current User',
          modifiedDate: now,
          currentStep: 'Prepare',
        };
        const savedBatch = await dataService.createRevaluationBatch(batchToSave);
        batchId = savedBatch.id;
      }
      
      // Call the backend PostBatch endpoint
      const result = await dataService.postRevaluationBatch(batchId!, 'Current User');
      
      // Check for posting failures
      const postingResult = result?.postingResult;
      const hasCreationFailures = postingResult?.transactionCreationFailures?.length > 0;
      const hasPostingFailures = postingResult?.financialsPostingFailures?.length > 0;
      
      if (hasCreationFailures || hasPostingFailures) {
        const failureMessages: string[] = [];
        const errorDetails: string[] = [];
        
        if (hasCreationFailures) {
          const count = postingResult.transactionCreationFailures.length;
          failureMessages.push(`${count} transaction(s) failed to create`);
          postingResult.transactionCreationFailures.forEach((failure: { assetCode: string; errorMessage: string }) => {
            errorDetails.push(`Asset '${failure.assetCode}': ${formatApiErrorMessage(failure.errorMessage)}`);
          });
        }
        
        if (hasPostingFailures) {
          const count = postingResult.financialsPostingFailures.length;
          failureMessages.push(`${count} transaction(s) failed to post to Financials`);
          postingResult.financialsPostingFailures.forEach((failure: { assetCode: string; errorMessage: string }) => {
            errorDetails.push(`Asset '${failure.assetCode}': ${formatApiErrorMessage(failure.errorMessage)}`);
          });
        }
        
        const maxErrorsToShow = 3;
        const displayedErrors = errorDetails.slice(0, maxErrorsToShow);
        const remainingCount = errorDetails.length - maxErrorsToShow;
        let description = `Reference: ${reference}. ${failureMessages.join('. ')}.\n${displayedErrors.join('\n')}`;
        if (remainingCount > 0) {
          description += `\n...and ${remainingCount} more error(s)`;
        }
        
        toast.warning('Revaluation Completed with Warnings', {
          description,
          duration: 10000,
        });
      } else {
        toast.success('Revaluation Created Successfully', {
          description: `Reference: ${reference}`,
        });
      }
      
      setShowProcessConfirm(false);
      onClose(); // Close the entry screen and return to the landing page
    } catch (error) {
      console.error('Failed to process revaluation batch:', error);
      toast.error('Failed to process revaluation batch', {
        description: 'Please ensure you are in API mode and try again.',
      });
      setShowProcessConfirm(false);
    }
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setShowCancelConfirm(true);
    } else {
      onClose();
    }
  };

  const confirmCancel = () => {
    setShowCancelConfirm(false);
    onClose();
  };

  const handleRemoveSelectedItems = () => {
    if (selectedItemIds.size === 0) return;
    setShowRemoveConfirm(true);
  };

  const confirmRemoveSelectedItems = () => {
    setItems(prevItems => prevItems.filter(item => !selectedItemIds.has(item.id)));
    setSelectedItemIds(new Set());
    setShowRemoveConfirm(false);
  };

  const handleAddMoreAssets = () => {
    // Navigate back to asset selection
    navigate('/transactions/revaluation', { 
      state: { openAssetSelection: true } 
    });
  };

  const handleUpdateItem = (itemId: string, updates: any) => {
    setItems(prevItems =>
      prevItems.map(item =>
        item.id === itemId ? { ...item, ...updates } : item
      )
    );
  };

  const handleReapplyDefaults = (applyToAll: boolean) => {
    const updatedItems = recalculateRevaluationItems(
      items,
      revaluationMode,
      revaluationAmount,
      applyToAll
    );

    setItems(updatedItems);

    toast.success('Defaults reapplied', {
      description: applyToAll 
        ? 'All lines have been recalculated from header values.'
        : 'Unmodified lines have been recalculated from header values.',
    });
  };

  const handleRevaluationModeChange = (value: RevaluationMode) => {
    // Check if any items have been manually overridden
    const hasOverrides = items.some(item => item.isOverridden);
    
    if (hasOverrides && items.length > 0) {
      toast.info('Defaults recalculated from header', {
        description: "Use 'Reapply Defaults' to update overridden lines.",
      });
    }
    
    setRevaluationMode(value);
  };

  const handleRevaluationAmountChange = (value: string) => {
    if (isReadOnly) return;
    
    // Allow empty string or parse the value (including negatives)
    const numValue = value === '' ? 0 : parseFloat(value);
    
    // Only validate if we have a valid number
    if (!isNaN(numValue)) {
      // Validate the amount
      const validation = validateRevaluationAmount(numValue, revaluationMode);
      if (!validation.isValid) {
        toast.error('Invalid amount', { description: validation.error });
        return;
      }
      
      setRevaluationAmount(numValue);
    }
  };

  const handleExportCSV = () => {
    if (items.length === 0) {
      toast.error('No data to export');
      return;
    }

    try {
      exportToCSV({
        data: items,
        columns: getRevaluationExportColumns(),
        filename: generateFilename(`revaluation-batch-${batchReference || 'draft'}`, 'csv'),
      });

      toast.success('Export successful', {
        description: 'Revaluation batch exported to CSV',
      });
    } catch (error) {
      console.error('Error exporting to CSV:', error);
      toast.error('Export failed', {
        description: error instanceof Error ? error.message : 'Unknown error occurred',
      });
    }
  };

  const handleExportExcel = () => {
    if (items.length === 0) {
      toast.error('No data to export');
      return;
    }

    try {
      exportToExcel({
        data: items,
        columns: getRevaluationExportColumns(),
        filename: generateFilename(`revaluation-batch-${batchReference || 'draft'}`, 'xlsx'),
        sheetName: 'Revaluation Details',
      });

      toast.success('Export successful', {
        description: 'Revaluation batch exported to Excel',
      });
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error('Export failed', {
        description: error instanceof Error ? error.message : 'Unknown error occurred',
      });
    }
  };

  return (
    <div className="flex h-full flex-col" style={{ backgroundColor: 'oklch(0.984 0.003 247.858)' }}>
      {/* Compact Header */}
      <div className="border-b bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm shadow-sm flex-shrink-0">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={handleCancel} className="h-9 w-9">
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div className="flex items-center gap-3">
                <TrendingUp className="h-5 w-5 text-primary" />
                <div>
                  <h1 className="text-xl font-bold">
                    {batchReference ? `Revaluation - ${batchReference}` : 'New Revaluation'}
                  </h1>
                  {isReadOnly && initialBatch?.postedDate ? (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Posted: {new Date(initialBatch.postedDate).toLocaleString()}
                    </p>
                  ) : initialBatch?.modifiedDate ? (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Last modified: {new Date(initialBatch.modifiedDate).toLocaleString()}
                    </p>
                  ) : null}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!isReadOnly && (
                <>
                  <Button variant="outline" onClick={handleSave} className="h-9">
                    <Save className="mr-2 h-4 w-4" />
                    Save
                  </Button>
                  <Button onClick={handleProcess} className="h-9">
                    <Play className="mr-2 h-4 w-4" />
                    Process
                  </Button>
                </>
              )}
              {isReadOnly && (
                <Badge variant="secondary" className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                  Completed
                </Badge>
              )}
              {items.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="icon" className="h-9 w-9">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={handleExportCSV}>
                      <Download className="mr-2 h-4 w-4" />
                      Export to CSV
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleExportExcel}>
                      <Download className="mr-2 h-4 w-4" />
                      Export to Excel
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto pb-20">
        <div className="space-y-4 p-4">
          {/* Header Details Card */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden rounded-lg">
            <div className="p-6">
              <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                Header Details
              </h3>
              <div className="grid grid-cols-12 gap-4">
                {/* Row 1: Reference, Revaluation Mode, Notes (right side) */}
                <div className="col-span-3">
                  <Label htmlFor="reference" className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                    Reference *
                  </Label>
                  <Input
                    id="reference"
                    value={batchReference}
                    onChange={(e) => setBatchReference(e.target.value.slice(0, 10))}
                    placeholder="Enter reference..."
                    maxLength={10}
                    readOnly={isReadOnly}
                    className="h-9 placeholder:italic"
                    style={{ fontStyle: 'normal' }}
                    tabIndex={1}
                  />
                </div>

                {/* Revaluation Mode */}
                <div className="col-span-3">
                  <Label htmlFor="revalue-mode" className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                    Revaluation Mode *
                  </Label>
                  <Select 
                    value={revaluationMode} 
                    onValueChange={handleRevaluationModeChange}
                    disabled={isReadOnly}
                    data-readonly={isReadOnly ? '' : undefined}
                  >
                    <SelectTrigger id="revalue-mode" className="h-9 placeholder:italic" style={{ fontStyle: 'normal' }}>
                      <SelectValue placeholder="Select mode" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ByPercentage">By Percentage</SelectItem>
                      <SelectItem value="ByValue">By Value</SelectItem>
                      <SelectItem value="ToValue">To Value</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Notes - Right side spanning 2 rows */}
                <div className="col-span-6 row-span-2">
                  <Label htmlFor="notes" className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                    Notes
                  </Label>
                  <Textarea
                    id="notes"
                    value={notes}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNotes(e.target.value.slice(0, 400))}
                    placeholder="Enter any additional notes..."
                    className="h-[calc(100%-1.75rem)] resize-none text-sm placeholder:italic"
                    style={{ fontStyle: 'normal' }}
                    maxLength={400}
                    readOnly={isReadOnly}
                    tabIndex={4}
                  />
                  <p className="text-xs text-muted-foreground text-right mt-1">{notes.length}/400</p>
                </div>

                {/* Row 2: Date, Revaluation Amount */}
                <div className="col-span-3">
                  <Label htmlFor="date" className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                    Date *
                  </Label>
                  <Input
                    id="date"
                    type="date"
                    value={dateOfRun}
                    onChange={(e) => setDateOfRun(e.target.value)}
                    readOnly={isReadOnly}
                    className="h-9 placeholder:italic"
                    style={{ fontStyle: 'normal' }}
                    tabIndex={2}
                  />
                </div>

                {/* Revaluation Amount */}
                <div className="col-span-3">
                  <Label htmlFor="revaluation-amount" className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                    Revaluation Amount *
                  </Label>
                  <Input
                    id="revaluation-amount"
                    type="number"
                    step={revaluationMode === 'ByPercentage' ? '0.0001' : '0.01'}
                    value={revaluationAmount}
                    onChange={(e) => handleRevaluationAmountChange(e.target.value)}
                    placeholder={getRevaluationAmountPlaceholder(revaluationMode)}
                    readOnly={isReadOnly}
                    className="h-9"
                    tabIndex={3}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Transaction Details Table */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden rounded-lg">
            <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                Transaction Details
                <Badge variant="secondary" className="ml-2">{items.length} {items.length === 1 ? 'Asset' : 'Assets'}</Badge>
              </h3>
              {!isReadOnly && (
                <div className="flex items-center gap-2">
                  {selectedItemIds.size > 0 && (
                    <Button type="button" variant="destructive" size="sm" onClick={handleRemoveSelectedItems} className="h-8">
                      <Trash2 className="mr-2 h-3.5 w-3.5" />
                      Remove Lines ({selectedItemIds.size})
                    </Button>
                  )}
                  {items.length > 0 && (
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleReapplyDefaults(false)} 
                      className="h-8"
                    >
                      <RefreshCw className="mr-2 h-3.5 w-3.5" />
                      Reapply Defaults
                    </Button>
                  )}
                  <Button type="button" variant="outline" size="sm" onClick={handleAddMoreAssets} className="h-8">
                    <Plus className="mr-2 h-3.5 w-3.5" />
                    Add Assets
                  </Button>
                </div>
              )}
            </div>

            {items.length > 0 ? (
              <div className="overflow-x-auto">
                <RevaluationBatchGrid
                  items={items}
                  onUpdateItem={handleUpdateItem}
                  onReapplyDefaults={handleReapplyDefaults}
                  selectedItemIds={selectedItemIds}
                  onSelectionChange={setSelectedItemIds}
                  readonly={isReadOnly}
                  revaluationMode={revaluationMode}
                  revaluationAmount={revaluationAmount}
                />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="rounded-full bg-muted p-3 mb-4">
                  <FileText className="h-6 w-6 text-muted-foreground" />
                </div>
                <h3 className="font-semibold text-lg mb-1">No Assets Added</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Add assets to this revaluation batch to get started
                </p>
                {!isReadOnly && (
                  <Button variant="outline" size="sm" onClick={handleAddMoreAssets}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add Assets
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Fixed Bottom Footer */}
      {items.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm border-t border-slate-200 dark:border-slate-800 shadow-lg z-10">
          <div className="px-6 py-3">
            <div className="flex justify-end items-center gap-4 max-w-7xl ml-auto">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-slate-600 dark:text-slate-400">Current NBV</span>
                <span className="text-base font-bold text-slate-900 dark:text-slate-100">{formatCurrency(totalCurrentNBV)}</span>
              </div>
              <div className="w-px h-6 bg-slate-300 dark:bg-slate-600"></div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-slate-600 dark:text-slate-400">NBV after Revaluation</span>
                <span className="text-base font-bold text-slate-900 dark:text-slate-100">{formatCurrency(totalRevaluedNBV)}</span>
              </div>
              <div className="w-px h-6 bg-slate-300 dark:bg-slate-600"></div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-slate-600 dark:text-slate-400">Adjustment</span>
                <span className={`text-base font-bold ${totalRevaluationAdjustment >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  {totalRevaluationAdjustment >= 0 ? '+' : ''}{formatCurrency(totalRevaluationAdjustment)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modals */}
      <ConfirmationModal
        open={showCancelConfirm}
        onOpenChange={setShowCancelConfirm}
        onConfirm={confirmCancel}
        title="Discard Changes?"
        description="You have unsaved changes. Are you sure you want to leave? All changes will be lost."
        confirmText="Discard Changes"
        cancelText="Continue Editing"
        type="warning"
      />

      <ConfirmationModal
        open={showProcessConfirm}
        onOpenChange={setShowProcessConfirm}
        onConfirm={confirmProcess}
        title="Process Revaluation Batch"
        description={
          <div className="space-y-2">
            <p>This will process the revaluation batch and create transactions.</p>
            <div className="mt-4 p-3 bg-muted rounded-md space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Assets:</span>
                <span className="font-medium">{items.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total Adjustment:</span>
                <span className={`font-medium ${totalRevaluationAdjustment >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {totalRevaluationAdjustment >= 0 ? '+' : ''}{formatCurrency(totalRevaluationAdjustment)}
                </span>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mt-3">This action cannot be undone.</p>
          </div>
        }
        confirmText="Process Batch"
        cancelText="Review Again"
        type="success"
      />

      <ConfirmationModal
        open={showRemoveConfirm}
        onOpenChange={setShowRemoveConfirm}
        onConfirm={confirmRemoveSelectedItems}
        title="Remove Selected Assets"
        description={`Are you sure you want to remove ${selectedItemIds.size} selected asset(s) from this batch?`}
        confirmText="Remove Assets"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
}
